/* eslint-disable no-undef */
// @flow
import type TrackingProperties from "../models/TrackingProperties";
import type {Country} from "../models/Country";
import {defaultCountry} from "../models/Country";
import type {DealerDetails} from "../models/DealerDetails";
import RootNodeProvider from "../helper/RootNodeProvider";
import type {GarageDealership} from "../models/GarageDealership";
import {selectLanguage} from "../helper/languageUtils";
import type {Role} from "../models/Role";
import {RoleTypes} from "../models/Role";
import {createSlice} from "@reduxjs/toolkit";

export type ConfigReducerState = {
    brandInstances?: GarageDealership[],
    theme: ?string,
    dealerId: ?string,
    dealerDetails: DealerDetails,
    disclaimer: ?string,
    environment: ?string,
    garageId: ?string,
    garageGroupId: ?string,
    dealerLanguage: ?string,
    moduleLanguage: string,
    name: ?string,
    trackingProperties: TrackingProperties,
    trackerLoaded: boolean,
    consentlessTrackerLoaded: boolean,
    configuredCountries: Country[],
    configLoaded: boolean,
    isShopEnabled: boolean,
    isLeadModuleEnabled: boolean,
    watchlistEnabled: boolean,
    productPricesDisabled: boolean,
    openDealerShopsInNewTab: boolean,
    isLoading: boolean,
    legalTerms: string,
    revocationTerms: string,
    revocationLink: string,
    role: ?Role,
    privacyPolicy: string,
    privacyPolicyLink: string,
    imprintText: string,
    imprintLink: string,
    contrastColor: string,
    defaultBrand: ?string, 		// optional auto config from root element
    defaultLocation: ?string,	// optional auto config from root element
    // group location named by the link that opened the page (?selected_location=, #57367)
    linkedLocation: ?string,
    showMsrp: boolean,
    // null = not configured (translated default), "" = headline hidden, otherwise rendered 1:1 (#56151)
    brandSelectOverrideHeadline: ?string,
    // the garage sells into the EU (computed by offering) - gates the guarantee notice and the GARAN label (#56424)
    euShop: boolean,
};

let garageGroupId = "";
let dealerId = "";
let garageId = "";
let environment = "";
let role = "";
let preferredLanguage = "";
let openDealerShopsInNewTab = false;
let defaultBrand = null;
let defaultLocation = null;
let linkedLocation = null;
let brandWhitelist = [];
let msrpDisabled = false;
let brandSelectOverrideHeadline = null;

const getInitialState = () => {
    // the central search links a group page with the location the customer picked, either
    // with or without brand prefix (?selected_location=6GB57673, #57367)
    linkedLocation = new URLSearchParams(window.location.search).get("selected_location") || null;
    if (RootNodeProvider.getRootNode() && RootNodeProvider.getRootNode().dataset) {
        dealerId = RootNodeProvider.getRootNode().dataset.dealerId;
        garageId = RootNodeProvider.getRootNode().dataset.garageId;
        garageGroupId = RootNodeProvider.getRootNode().dataset.garageGroupId;
        preferredLanguage = RootNodeProvider.getRootNode().dataset.language;
        role = RootNodeProvider.getRootNode().dataset.role || (garageId && RoleTypes.GARAGE) || (garageGroupId && RoleTypes.GARAGE_GROUP) || RoleTypes.DEALER;
        openDealerShopsInNewTab = "openDealerInNewTab" in RootNodeProvider.getRootNode().dataset;
        environment = RootNodeProvider.getRootNode().dataset.environment ? RootNodeProvider.getRootNode().dataset.environment : "default";
        defaultBrand = RootNodeProvider.getRootNode().dataset.defaultBrand ? RootNodeProvider.getRootNode().dataset.defaultBrand : null;
        defaultLocation = RootNodeProvider.getRootNode().dataset.defaultLocation ? RootNodeProvider.getRootNode().dataset.defaultLocation : null;
        brandWhitelist = RootNodeProvider.getRootNode().dataset.brandWhitelist ? RootNodeProvider.getRootNode().dataset.brandWhitelist.split(",") : [];
        msrpDisabled = "disableMsrp" in RootNodeProvider.getRootNode().dataset;
        // keep "" distinct from "not set": an empty value hides the headline entirely
        brandSelectOverrideHeadline = RootNodeProvider.getRootNode().dataset.brandselectOverrideHeadline ?? null;
    }

    return {
        brandInstances: null,
        dealerId,
        dealerDetails: null,
        disclaimer: null,
        environment,
        garageId,
        garageGroupId,
        dealerLanguage: null,
        moduleLanguage: preferredLanguage || navigator.language || "en",
        name: null,
        role,
        theme: "default",
        trackingProperties: {
            trackerLanguage: null,
            analyticsTrackers: [],
            consentlessTrackers: []
        },
        trackerLoaded: false,
        consentlessTrackerLoaded: false,
        configLoaded: false,
        isShopEnabled: false,
        isLeadModuleEnabled: false,
        productPricesDisabled: true,
        watchlistEnabled: false,
        openDealerShopsInNewTab,
        configuredCountries: [],
        isLoading: false,
        legalTerms: "",
        revocationTerms: "",
        revocationLink: "",
        privacyPolicy: "",
        imprintText: "",
        contrastColor: "#ffffff",
        defaultBrand,
        defaultLocation,
        linkedLocation,
        brandWhitelist,
        showMsrp: !msrpDisabled,
        brandSelectOverrideHeadline,
        euShop: false,
    };

};

const configSlice = createSlice({
    name: "config",
    initialState: getInitialState(),
    reducers: {
        moduleInit() {
            return getInitialState();
        },
        configSet(state, action) {
            state.isShopEnabled = action.payload.isShopEnabled;
            state.configuredCountries = action.payload.configuredCountries && action.payload.configuredCountries.length > 0
                ? action.payload.configuredCountries
                : [defaultCountry];
            state.isLeadModuleEnabled = action.payload.isLeadModuleEnabled;
            state.watchlistEnabled = state.role !== RoleTypes.DISTRIBUTOR && action.payload.watchlistEnabled;
            state.role = state.role === RoleTypes.DISTRIBUTOR ? RoleTypes.DISTRIBUTOR : (state.garageGroupId ? RoleTypes.GROUP_DEALER : RoleTypes.DEALER);
            state.productPricesDisabled = action.payload.productPricesDisabled;
            state.dealerId = action.payload.dealerId;
            state.dealerDetails = action.payload.dealerDetails;
            state.disclaimer = action.payload.disclaimer;
            state.garageId = action.payload.garageId || garageId;
            state.dealerLanguage = action.payload.dealerLanguage;
            state.moduleLanguage = selectLanguage(preferredLanguage, action.payload.availableLanguages);
            state.name = action.payload.name;
            state.theme = action.payload.theme;
            state.configLoaded = true;
            state.trackingProperties = action.payload.trackingProperties;
            state.legalTerms = action.payload.legalTerms;
            state.revocationTerms = action.payload.revocationTerms;
            state.revocationLink = action.payload.revocationLink;
            state.privacyPolicy = action.payload.privacyPolicy;
            state.imprintText = action.payload.imprintText;
            state.imprintText = action.payload.imprintText;
            state.defaultBrand = null;
            state.imprintLink = action.payload.imprintLink;
            state.privacyPolicyLink = action.payload.privacyPolicyLink;
            state.euShop = !!action.payload.euShop;
        },
        consentGoogleAnalyticsRevoked(state) {
            state.trackerLoaded = false;
        },
        configDisabled(state) {
            state.configLoaded = false;
        },
        brandInstanceGotten(state, action) {
            if (brandWhitelist.length) {
                state.brandInstances = action.payload.filter(brandInstance => brandWhitelist.includes(brandInstance.manufacturerName));
            } else {
                state.brandInstances = action.payload;
            }
        },
        brandInstanceErrorGotten(state) {
        },
        garageGroupIdResolved(state, action) {
            state.garageGroupId = action.payload;
        },
        // signal for the tracker middleware; the selection itself is handled by setBrandInstance
        brandSelected(state, action) {
        },
        trackerSuccessLoaded(state) {
            state.trackerLoaded = true;
        },
        trackerWithoutConsentSuccessLoaded(state) {
            state.consentlessTrackerLoaded = true;
        },
        contrastColorCalculated(state, action) {
            state.contrastColor = action.payload;
        },
        configErrorFetched(state, action) {
        }
    }
});

export const {
    configSet,
    contrastColorCalculated,
    trackerWithoutConsentSuccessLoaded,
    trackerSuccessLoaded,
    brandInstanceGotten,
    brandSelected,
    configDisabled,
    consentGoogleAnalyticsRevoked,
    moduleInit,
    brandInstanceErrorGotten,
    garageGroupIdResolved,
    configErrorFetched
} = configSlice.actions;

export default configSlice.reducer;
